import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, HelpCircle, AlertCircle, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useAuth } from '../../context/AuthContext';

const FieldHelp = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export default function ContactsStep({ initialData, onNext, onBack, saving, planId }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState(() => ({
    admin_nombre: initialData?.admin_nombre || user?.full_name || '',
    admin_email: initialData?.admin_email || user?.email || '',
    admin_telefono: initialData?.admin_telefono || '',
    contacto_secundario_nombre: initialData?.contacto_secundario_nombre || '',
    contacto_secundario_email: initialData?.contacto_secundario_email || '',
    gestoria_nombre: initialData?.gestoria_nombre || '',
    gestoria_contacto: initialData?.gestoria_contacto || '',
    gestoria_email: initialData?.gestoria_email || '',
    gestoria_telefono: initialData?.gestoria_telefono || '',
  }));
  const [errors, setErrors] = useState({});

  // Check if user has Pack 3 (Administración Completa) - requires gestoría fields
  const requiresGestoria = planId === 'pkg-administracion-completa';

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.admin_nombre || formData.admin_nombre.length < 3) {
      newErrors.admin_nombre = 'El nombre del administrador es obligatorio (mín. 3 caracteres)';
    }
    if (!formData.admin_email) {
      newErrors.admin_email = 'El email del administrador es obligatorio';
    } else if (!/\S+@\S+\.\S+/.test(formData.admin_email)) {
      newErrors.admin_email = 'Email inválido';
    }
    
    if (formData.contacto_secundario_email && !/\S+@\S+\.\S+/.test(formData.contacto_secundario_email)) {
      newErrors.contacto_secundario_email = 'Email inválido';
    }
    
    // Validate gestoría fields if required (Pack 3)
    if (requiresGestoria) {
      if (!formData.gestoria_nombre) {
        newErrors.gestoria_nombre = 'El nombre de la gestoría es obligatorio para tu plan';
      }
      if (!formData.gestoria_contacto) {
        newErrors.gestoria_contacto = 'El contacto de la gestoría es obligatorio para tu plan';
      }
      if (!formData.gestoria_email) {
        newErrors.gestoria_email = 'El email de la gestoría es obligatorio para tu plan';
      } else if (!/\S+@\S+\.\S+/.test(formData.gestoria_email)) {
        newErrors.gestoria_email = 'Email inválido';
      }
    } else {
      // Validate optional gestoría email format if provided
      if (formData.gestoria_email && !/\S+@\S+\.\S+/.test(formData.gestoria_email)) {
        newErrors.gestoria_email = 'Email inválido';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onNext(formData);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Usuarios y Contactos</h2>
        <p className="text-slate-600">Configura los contactos principales de tu empresa</p>
      </div>

      {/* Administrador Principal */}
      <div className="bg-slate-50 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-primary" />
          Administrador Principal
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-2 mb-2">
              Nombre Completo <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData.admin_nombre}
              onChange={(e) => handleChange('admin_nombre', e.target.value)}
              placeholder="Juan García López"
              className={errors.admin_nombre ? 'border-red-500' : ''}
              data-testid="input-admin-nombre"
            />
            {errors.admin_nombre && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.admin_nombre}
              </p>
            )}
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              type="email"
              value={formData.admin_email}
              onChange={(e) => handleChange('admin_email', e.target.value)}
              placeholder="admin@empresa.com"
              className={errors.admin_email ? 'border-red-500' : ''}
              data-testid="input-admin-email"
            />
            {errors.admin_email && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.admin_email}
              </p>
            )}
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              Teléfono
              <FieldHelp text="Teléfono de contacto del administrador" />
            </Label>
            <Input
              value={formData.admin_telefono}
              onChange={(e) => handleChange('admin_telefono', e.target.value)}
              placeholder="+34 612 345 678"
              data-testid="input-admin-telefono"
            />
          </div>
        </div>
      </div>

      {/* Contacto Secundario */}
      <div className="bg-slate-50 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-slate-500" />
          Contacto Secundario (Opcional)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-2">Nombre</Label>
            <Input
              value={formData.contacto_secundario_nombre}
              onChange={(e) => handleChange('contacto_secundario_nombre', e.target.value)}
              placeholder="María López"
              data-testid="input-contacto-secundario-nombre"
            />
          </div>

          <div>
            <Label className="mb-2">Email</Label>
            <Input
              type="email"
              value={formData.contacto_secundario_email}
              onChange={(e) => handleChange('contacto_secundario_email', e.target.value)}
              placeholder="maria@empresa.com"
              className={errors.contacto_secundario_email ? 'border-red-500' : ''}
              data-testid="input-contacto-secundario-email"
            />
            {errors.contacto_secundario_email && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.contacto_secundario_email}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Datos de Gestoría */}
      <div className={`rounded-xl p-5 ${requiresGestoria ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
        <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <UserPlus className={`w-5 h-5 ${requiresGestoria ? 'text-amber-600' : 'text-slate-500'}`} />
          Datos de la Gestoría
          {requiresGestoria && (
            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
              Obligatorio para tu plan
            </span>
          )}
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          {requiresGestoria 
            ? 'Tu plan incluye cierre mensual y envío a gestoría. Necesitamos los datos de contacto.'
            : 'Si trabajas con una gestoría, puedes añadir sus datos aquí (opcional).'
          }
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-2 mb-2">
              Nombre de la Gestoría {requiresGestoria && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={formData.gestoria_nombre}
              onChange={(e) => handleChange('gestoria_nombre', e.target.value)}
              placeholder="Gestoría García & Asociados"
              className={errors.gestoria_nombre ? 'border-red-500' : ''}
              data-testid="input-gestoria-nombre"
            />
            {errors.gestoria_nombre && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.gestoria_nombre}
              </p>
            )}
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              Persona de Contacto {requiresGestoria && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={formData.gestoria_contacto}
              onChange={(e) => handleChange('gestoria_contacto', e.target.value)}
              placeholder="Pedro Sánchez"
              className={errors.gestoria_contacto ? 'border-red-500' : ''}
              data-testid="input-gestoria-contacto"
            />
            {errors.gestoria_contacto && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.gestoria_contacto}
              </p>
            )}
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              Email de la Gestoría {requiresGestoria && <span className="text-red-500">*</span>}
            </Label>
            <Input
              type="email"
              value={formData.gestoria_email}
              onChange={(e) => handleChange('gestoria_email', e.target.value)}
              placeholder="contacto@gestoria.com"
              className={errors.gestoria_email ? 'border-red-500' : ''}
              data-testid="input-gestoria-email"
            />
            {errors.gestoria_email && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.gestoria_email}
              </p>
            )}
          </div>

          <div>
            <Label className="mb-2">Teléfono de la Gestoría</Label>
            <Input
              value={formData.gestoria_telefono}
              onChange={(e) => handleChange('gestoria_telefono', e.target.value)}
              placeholder="+34 912 345 678"
              data-testid="input-gestoria-telefono"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack} data-testid="btn-back">
          <ChevronLeft className="w-4 h-4 mr-2" /> Atrás
        </Button>
        <Button onClick={handleSubmit} disabled={saving} data-testid="btn-next">
          {saving ? 'Guardando...' : 'Continuar'} <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
